import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setView from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetView";
import {
    getProductListDataView,
    getProductListDataViewProperty,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductListPageContext } from "@insite/content-library/Pages/ProductListPage";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Grid from "@insite/mobius/Icons/Grid";
import List from "@insite/mobius/Icons/List";
import Table from "@insite/mobius/Icons/Table";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    loaded: !!getProductListDataView(state).value,
    view: state.pages.productList.view || getSettingsCollection(state).productSettings.defaultViewType,
    pagination: getProductListDataViewProperty(state, "pagination"),
});

const mapDispatchToProps = {
    setView,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListViewSelectStyles {
    wrapper?: InjectableCss;
    listViewClickable?: ClickablePresentationProps;
    listViewIcon?: IconPresentationProps;
    gridViewClickable?: ClickablePresentationProps;
    gridViewIcon?: IconPresentationProps;
    tableViewClickable?: ClickablePresentationProps;
    tableViewIcon?: IconPresentationProps;
}

export const viewSelectStyles: ProductListViewSelectStyles = {
    wrapper: {
        css: css`
            display: flex;
            padding-top: 5px;
            [data-test-selector="viewSelectList"],
            [data-test-selector="viewSelectGrid"],
            [data-test-selector="viewSelectTable"] {
                &:focus {
                    outline: none;
                }
                [class^="IconWrapper"] {
                    color: #ccc;
                }
                [class^="IconWrapper"].active {
                    opacity: 1;
                    svg {
                        background-color: #ef3742;
                        color: #fff;
                        border-radius: 4px;
                    }
                }
            }
        `,
    },
    listViewClickable: {
        css: css`
            margin-right: 10px;
        `,
    },
    gridViewClickable: {
        css: css`
            margin-right: 20px;
        `,
    },
    listViewIcon: {
        src: List,
        size: 25,
    },
    gridViewIcon: {
        src: Grid,
        size: 25,
    },
    tableViewIcon: {
        src: Table,
        size: 26,
    },
};

const styles = viewSelectStyles;

class ProductListViewSelect extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const view = getCookie("ProductList-View");
        if (view) {
            this.props.setView({ view: view as ProductListViewType });
        }
    }

    render() {
        const { loaded, view, setView } = this.props;

        if (!loaded) {
            return null;
        }

        const clickListViewHandler = () => {
            const dataLayer = (window as any).dataLayer || [];
            if (dataLayer) {
                dataLayer.push({ ecommerce: null });
                dataLayer.push({
                    event: "plp_view_selection",
                    event_Parameter: "list_view",
                });
            }
            // console.log("List View ", dataLayer);
            changeView("List");
        };

        const clickGridViewHandler = () => {
            const dataLayer = (window as any).dataLayer || [];
            if (dataLayer) {
                dataLayer.push({ ecommerce: null });
                dataLayer.push({
                    event: "plp_view_selection",
                    event_Parameter: "grid_view",
                });
            }
            // console.log("Grid View", dataLayer);
            changeView("Grid");
        };

        const clickTableViewHandler = () => {
            changeView("Table");
        };

        const changeView = (view: ProductListViewType) => {
            setCookie("ProductList-View", view);
            setView({ view });
        };

        return (
            <StyledWrapper {...styles.wrapper}>
                <Clickable
                    data-test-selector="viewSelectList"
                    {...styles.listViewClickable}
                    onClick={clickListViewHandler}
                >
                    <Icon
                        {...styles.listViewIcon}
                        color={view === "List" ? "primary" : undefined}
                        className={view === "List" ? "active" : ""}
                    />
                </Clickable>
                <Clickable
                    data-test-selector="viewSelectGrid"
                    {...styles.gridViewClickable}
                    onClick={clickGridViewHandler}
                >
                    <Icon
                        {...styles.gridViewIcon}
                        color={view === "Grid" ? "primary" : undefined}
                        className={view === "Grid" ? "active" : ""}
                    />
                </Clickable>
                {/* Toggle to Table view functionality in PLP Page */}
                {/* <Clickable
                    data-test-selector="viewSelectTable"
                    {...styles.gridViewClickable}
                    onClick={clickTableViewHandler}
                >
                    <Icon
                        {...styles.tableViewIcon}
                        color={view === "Table" ? "primary" : undefined}
                        className={view === "Grid" ? "active" : ""}
                    />
                </Clickable> */}
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListViewSelect),
    definition: {
        group: "Product List",
        displayName: "View Select Custom",
        allowedContexts: [ProductListPageContext],
    },
};

export default widgetModule;
